// Taşeron portalı — İzin detayı: Kapsanan Personel + **readiness** (api-contract §21/§17/§22).
// Yetkili **kendi personelini** izne atar/çıkarır. Araçlar sekmesinin düzeni (#12): tablonun üstünde
// istenen personel belgeleri (gri etiketler + ek talep notu), satır başına Ad soyad | Eğitim n/m |
// Belgeler n/m | Hazır | ⋮. Belgeler = `satisfiedDocumentCount` / `requiredDocumentCount` (§14
// `WorkPermitPersonnelDto`, yalnız görünürlük — supplier burada belge yüklemez); m = 0 iken soluk
// "—". Kutu "m"nin kümesini gösterir: tip ∪ §30.10 ek talepler = ilk satırın `requestedDocuments`'ı
// (her satırda aynı türler ve notlar, yalnız `satisfied` değişir). Personel yokken izin düzeyinde
// birleşik küme alanı olmadığından tipin `requiredDocuments`'ına düşer — ek talepler o an görünmez.
// Ata (§21.2 `POST /personnel`) + kapsamdan çıkar (`DELETE /personnel/{userUuid}`, ⋮ menüsünden).
// Aksiyon sonrası izin detayı + liste invalidate (personel + readiness tek `GET
// /supplier/permits/{uuid}` içinde döner). Personel verisi üst detay sorgusundan gelir (senkron).
// Ad hücresi bir bağlantı DEĞİL (GL5): Personelim'le ORTAK `PersonnelDrawer`'ı izin bağlamıyla
// YERİNDE açan bir `<button>` (URL değişmez). Çekmece açıkken kişi kapsamdan çıkarsa (başka sekme)
// çekmece kapanır ve odak "Personel ata"ya iner; kişi yalnız Personelim listesinde yoksa çekmece AÇIK
// kalır ve gövdede "bulunamadı" bildirimi gösterir (izin bağlamı, bkz. `PersonnelDrawer` başlığı).
//
// **Düzenleme (#29).** Kişi kaydını düzenleme oturumu (`usePersonnelEditor` + `usePersonnelClient`)
// BU panelde yaşar ve çekmeceye geçer; "Düzenle" YALNIZ çekmecededir (hedefini Personelim listesinden
// kurar). Satırın ⋮'sine bilinçli olarak eklenmez: o menü kişinin bu izindeki yerine (kapsam) dair
// eylemleri taşır, firmanın personel kaydına değil. Kaydetme izin detayını da tazeler (ad globaldir).

import {
  ConfirmDialog,
  EmptyState,
  RowActionsMenu,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableToolbar,
  Tag,
  type WorkPermitPersonnelDto,
  awaitsActivation,
  cn,
  countReadyPersonnel,
  formatProgress,
  personLabel,
  supplierPermitApi,
  useAuthStore,
  usePersonnelEditor,
  useRowRemovalFocus,
} from '@isgportal/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { type RefObject, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersonnelClient } from '../../lib/personnelClient';
import { supplierPermitKeys } from '../../lib/supplierPermitKeys';
import { PersonnelDrawer } from '../personnel/PersonnelDrawer';
import { readinessColor } from '../status/tone';
import { PermitPersonnelAddModal } from './PermitPersonnelAddModal';
import { RequiredDocumentsList } from './RequiredDocumentsList';

interface PermitPersonnelPanelProps {
  permitUuid: string;
  personnel: WorkPermitPersonnelDto[];
  /**
   * İznin tipinde istenen personel belge türleri — `personnel` entity'sinin §27 kayıt kodları (§21.1).
   * Yalnız tip boyutu (§30.10 ek talepleri İÇERMEZ) → kutu bunu yalnız personel yokken gösterir.
   */
  requiredDocuments: string[];
  /** `personnel`'ı taşıyan izin detayı sonucunun zaman damgaları (`PermitVehiclesPanel.source` ile aynı sebep). */
  source: { dataUpdatedAt: number; errorUpdatedAt: number };
  /** "Personel ata" düğmesi — üst sayfa odağı buraya indirebilsin (sekme kalkınca). */
  addButtonRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Onay metni: bu sekmede arama yok, aynı adlı iki kişi iletişimle ayrılır — "Ad (iletişim)" yalnız
 * ikisi de varken; aksi hâlde ortak `personLabel` (adsız kişide iletişim).
 */
function removeConfirmName(p: WorkPermitPersonnelDto): string {
  const name = personLabel({ firstName: p.firstName, lastName: p.lastName });
  return name && p.emailOrPhone ? `${name} (${p.emailOrPhone})` : personLabel(p);
}

export function PermitPersonnelPanel({
  permitUuid,
  personnel,
  requiredDocuments,
  source,
  addButtonRef,
}: PermitPersonnelPanelProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);

  const [addOpen, setAddOpen] = useState(false);
  const [toRemove, setToRemove] = useState<WorkPermitPersonnelDto | null>(null);
  // Çekmece hedefi satır DEĞİL uuid: satır her render'da `personnel`'dan bulunur, hazırlık tazelenir.
  const [drawerUuid, setDrawerUuid] = useState<string | null>(null);
  const addButtonEl = useRef<HTMLButtonElement | null>(null);
  // Çekmece kapanınca odağın döneceği düğüm — açan satırın ad düğmesi (`Sheet` Trigger taşımaz).
  const openerRef = useRef<HTMLElement | null>(null);
  const nameButtons = useRef(new Map<string, HTMLButtonElement>());
  const triggers = useRef(new Map<string, HTMLButtonElement>());
  const rowKeys = useMemo(() => personnel.map((p) => p.uuid), [personnel]);
  const drawerRow = drawerUuid ? (personnel.find((p) => p.uuid === drawerUuid) ?? null) : null;

  const editor = usePersonnelEditor({
    client: usePersonnelClient(companyId),
    onCloseDrawer: () => setDrawerUuid(null),
  });

  // Çekmece açıkken kişi kapsamdan çıktı (başka sekme + yenileme): boş çekmece göstermeden kapat,
  // odak "Personel ata"ya (useRowRemovalFocus inişi), <main>'e değil.
  useEffect(() => {
    if (drawerUuid !== null && drawerRow === null) {
      openerRef.current = addButtonEl.current;
      setDrawerUuid(null);
    }
  }, [drawerUuid, drawerRow]);

  const focus = useRowRemovalFocus({
    rowKeys,
    stamps: source,
    addButtonRef: addButtonEl,
    dialogOpen: toRemove !== null,
    otherSurfaceOpen: addOpen || drawerUuid !== null,
  });

  // Panelin kendi ref'i (odak makinesi) + sayfanınki (sekme kalkınca odak geri dönüşü).
  function setAddButton(el: HTMLButtonElement | null) {
    addButtonEl.current = el;
    if (addButtonRef) {
      addButtonRef.current = el;
    }
  }

  function invalidatePermit() {
    queryClient.invalidateQueries({
      queryKey: supplierPermitKeys.detail(companyId, permitUuid),
    });
    queryClient.invalidateQueries({ queryKey: supplierPermitKeys.list(companyId) });
  }

  const removeMutation = useMutation({
    mutationFn: (userUuid: string) =>
      supplierPermitApi.removeSupplierPermitPersonnel(permitUuid, userUuid),
    onSuccess: (_data, userUuid) => {
      invalidatePermit();
      focus.removed(userUuid);
      setToRemove(null);
    },
    // 404 PERSONNEL_NOT_FOUND: kişi zaten kapsamda değil (bayat liste) → tazele; satır yenilemede
    // kalkar, odak talebi kurulur (Araçlar sekmesiyle aynı). Diyalog hatayı gösterir.
    onError: (_error, userUuid) => {
      invalidatePermit();
      focus.removeFailed(userUuid);
    },
  });

  const readyCount = countReadyPersonnel(personnel);
  // "m"nin kümesi (bkz. dosya başı): her satır aynı türleri ve notları taşır.
  const requestedDocuments =
    personnel[0]?.requestedDocuments ??
    requiredDocuments.map((documentType) => ({ documentType, note: null }));

  // S37-4: Carbon Tile gövdesi — `TableToolbar` (özet + "Personel ata" = bu sekmenin eylemi) +
  // istenen belgeler + tam-genişlik `Table`. Üst `Card` dolgusu sıfırdır (`carbon:p-0`, PermitDetailPage).
  return (
    <div className="flex flex-col">
      <TableToolbar
        summary={
          personnel.length > 0
            ? t('permits.readiness.summary', { ready: readyCount, total: personnel.length })
            : undefined
        }
        onNew={() => setAddOpen(true)}
        newLabel={t('permits.personnel.add')}
        newButtonRef={setAddButton}
      />

      <RequiredDocumentsList
        heading={t('permits.personnel.requiredDocuments')}
        emptyText={t('permits.personnel.requiredDocumentsEmpty')}
        documents={requestedDocuments}
        entity="personnel"
      />

      {/* `relative`: başlıktaki `sr-only` metin taşan tabloda kaydırıcıdan kaçıp sayfayı kaydırmasın.
          Ad soyad sütunu kalan genişliği alır ve kırpılır (≥sm `w-full` + `max-w-0`); düğme
          `w-fit` → alt çizgi/odak halkası yalnız metni kaplar, boş hücreyi değil. <sm sabit 10rem. */}
      <Table caption={t('permits.detail.section.personnel')} className="relative">
        <TableHeader>
          <TableRow>
            <TableHead
              className="w-full truncate sm:max-w-0"
              title={t('permits.personnel.col.name')}
            >
              {t('permits.personnel.col.name')}
            </TableHead>
            <TableHead className="text-right">{t('permits.personnel.col.training')}</TableHead>
            <TableHead className="text-right">{t('permits.personnel.col.documents')}</TableHead>
            <TableHead>{t('permits.personnel.col.readiness')}</TableHead>
            <TableHead className="w-12">
              <span className="sr-only">{t('common.actions')}</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {personnel.length === 0 ? (
            <TableMessageRow colSpan={5}>
              <EmptyState icon={Users} title={t('permits.personnel.empty')} />
            </TableMessageRow>
          ) : (
            personnel.map((p) => {
              const label = personLabel(p);
              return (
                <TableRow key={p.uuid}>
                  {/* Bu sekmede iletişim sütunu yok → adsız kişide düğme iletişimi gösterir
                      (`personLabel`). İkisi de yoksa boş bir düğme değil, soluk "—". */}
                  <TableCell truncate={false} className="sm:max-w-0">
                    {label ? (
                      <button
                        type="button"
                        ref={(el) => {
                          if (el) {
                            nameButtons.current.set(p.uuid, el);
                          } else {
                            nameButtons.current.delete(p.uuid);
                          }
                        }}
                        onClick={(e) => {
                          openerRef.current = e.currentTarget;
                          setDrawerUuid(p.uuid);
                        }}
                        title={label}
                        className="block w-40 truncate text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-fit sm:max-w-full"
                      >
                        {label}
                      </button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </TableCell>
                  <TableCell
                    className={cn(
                      'whitespace-nowrap text-right tnum',
                      p.requiredTrainingCount === 0 && 'text-muted',
                    )}
                  >
                    {formatProgress(p.completedTrainingCount, p.requiredTrainingCount)}
                  </TableCell>
                  <TableCell
                    className={cn('tnum text-right', p.requiredDocumentCount === 0 && 'text-muted')}
                  >
                    {formatProgress(p.satisfiedDocumentCount, p.requiredDocumentCount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      <Tag color={readinessColor(p.ready)}>
                        {p.ready ? t('permits.personnel.ready') : t('permits.personnel.notReady')}
                      </Tag>
                      {!p.ready && awaitsActivation(p) ? (
                        <Tag color="red">{t('permits.personnel.notActivated')}</Tag>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="w-12 px-2">
                    <RowActionsMenu
                      ariaLabel={t('permits.personnel.action.openMenu', { name: label })}
                      triggerRef={(el) => {
                        if (el) {
                          triggers.current.set(p.uuid, el);
                        } else {
                          triggers.current.delete(p.uuid);
                        }
                      }}
                      items={[
                        {
                          key: 'remove',
                          label: t('permits.personnel.action.remove'),
                          destructive: true,
                          onSelect: () => {
                            removeMutation.reset();
                            focus.rememberOpener(
                              nameButtons.current.get(p.uuid) ??
                                triggers.current.get(p.uuid) ??
                                addButtonEl.current,
                            );
                            setToRemove(p);
                          },
                        },
                      ]}
                    />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <PermitPersonnelAddModal
        open={addOpen}
        onOpenChange={setAddOpen}
        permitUuid={permitUuid}
        coveredUuids={rowKeys}
        restoreFocusRef={addButtonEl}
      />

      <PersonnelDrawer
        userUuid={drawerUuid}
        hint={drawerRow ?? undefined}
        permit={
          drawerRow
            ? {
                ready: drawerRow.ready,
                missingDocumentTypes: drawerRow.missingDocumentTypes,
                completedTrainingCount: drawerRow.completedTrainingCount,
                requiredTrainingCount: drawerRow.requiredTrainingCount,
                activated: drawerRow.activated,
              }
            : undefined
        }
        onOpenChange={(open) => !open && setDrawerUuid(null)}
        onTargetGone={() => {
          openerRef.current = addButtonEl.current;
        }}
        restoreFocusRef={openerRef}
        editor={editor}
      />

      <ConfirmDialog
        open={toRemove !== null}
        onOpenChange={(open) => !open && setToRemove(null)}
        title={t('permits.personnel.remove.title')}
        body={t('permits.personnel.remove.body', {
          name: toRemove ? removeConfirmName(toRemove) : '',
        })}
        confirmLabel={t('permits.personnel.remove.confirm')}
        restoreFocusRef={focus.menuRestoreRef}
        loading={removeMutation.isPending}
        error={removeMutation.isError ? removeMutation.error : undefined}
        onConfirm={() => toRemove && removeMutation.mutate(toRemove.uuid)}
      />
    </div>
  );
}
